import moment from "moment-timezone";
import React, { useCallback, useEffect, useState } from "react";
import { EventProps, Views } from "react-big-calendar";
import { EventItem } from "./types";
import Calendar from "./Calender";
import AppointmentEvent from "./AppointmentEvent";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./calender.scss";
import BlockoutEvent from "./BlockoutEvent";
import baseUrl from "../../../../api/baseUrl";
import AppointmentsSchedule from "../components/AppointmentsSchedule";
import { toast } from "react-toastify";
import ReScheduleTray from "../components/ReScheduleTray";
import { useDispatch, useSelector } from "react-redux";
import {
  setloadeActive,
  setloadeDeactive,
} from "../../../../redux/loader/loaderActions";
import { setTimeInterval } from "../../../../redux/appointment/appointmentAction";
import { RootState } from "../../../../redux/store";
import NewAppointment from "./NewAppointment";
import OnlineAppointmentModal from "./OnlineAppointmentModal";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PersonIcon from "@mui/icons-material/Person";

type Keys = keyof typeof Views;
const STEP = 5;
const TIME_SLOTS = 30 / STEP;

const formatDate = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/** Match clicked calendar time to dentist general-hours row (same logic as slot availability). */
function slotMetaForSlot(
  timeSlots: any[] | undefined,
  slotStart: Date
): { sessionName?: string; roomNumber: string } {
  const slots = timeSlots ?? [];
  const day = slotStart.getDay();
  const hours = slotStart.getHours();
  const minutes = slotStart.getMinutes();
  const match = slots.find((interval: any) => {
    const intStart = interval.start;
    const intEnd = interval.end;
    if (interval.day !== day) return false;
    const afterStart =
      hours > intStart.hours ||
      (hours === intStart.hours && minutes >= intStart.minutes);
    const beforeEnd =
      hours < intEnd.hours ||
      (hours === intEnd.hours && minutes < intEnd.minutes);
    return afterStart && beforeEnd;
  });
  if (!match) return { roomNumber: "" };
  const sessionRaw = match?.sessionName;
  const sessionName =
    sessionRaw == null ? undefined : String(sessionRaw).trim() || undefined;
  const roomRaw = match?.roomNumber;
  const roomNumber = roomRaw == null ? "" : String(roomRaw).trim();
  return { sessionName, roomNumber };
}

export default function CusomizeCalender() {
  const dispatch = useDispatch();
  const [date, setDate] = useState<Date>(moment().toDate());
  const [view, setView] = useState<(typeof Views)[Keys]>(Views.WEEK);
  const [appointmentDetail, setAppointmentDetail] = useState({});
  const [events, setEvents] = useState<any[]>([]);
  const [timeSlots, setTimeSlots] = useState<any[]>([]);
  const [tray, setTray] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [doctor, setDoctor] = useState("all");
  const [selectReSchedule, setSelectReSchedule] = useState("");
  const [appointmentType, setAppointmentType] = useState("schedule");
  const [appointmentModal, setAppointmentModal] = useState(false);
  const [newAppointmentModal, setNewAppointmentModal] = useState(false);
  const [newappointmentDetail, setNewAppointmentDetail] = useState({});
  const [onlineAppointmentModal, setOnlineAppointmentModal] = useState(false);
  const [onlineAppointmentDetail, setOnlineAppointmentDetail] = useState({});

  const formatTime = useCallback((dates?: any) => {
    const date = new Date(dates);
    return `${String(date.getHours()).padStart(2, "0")}:${String(
      date.getMinutes()
    ).padStart(2, "0")}:${String(date.getSeconds()).padStart(2, "0")}`;
  }, []);

  const newInterval = useSelector((state: RootState) => state.appointment);
  const onlineappointment = useSelector(
    (state: RootState) => state.makeappointment.onlineappointment
  );

  const components: any = {
    event: ({ event }: EventProps<EventItem>) => {
      const data = event?.data;

      if (data?.appointment) {
        // Attach event data to appointment for date/time extraction
        const appointmentWithEvent = {
          ...data?.appointment,
          _eventStart: event?.start,
          _eventEnd: event?.end,
        };
        return (
          <AppointmentEvent
            reschedule={(e: any) => reschedule(e)}
            appointment={appointmentWithEvent}
            isMonthView={view === Views.MONTH}
            onOnlineAppointmentClick={handleOnlineAppointmentClick}
          />
        );
      }
      if (data?.blockout) {
        return <BlockoutEvent blockout={data?.blockout} />;
      }
      return null;
    },
  };

  const fetchAppointments = useCallback((doctorId: any, centerDate?: Date) => {
    const d = centerDate || date;
    const start = moment(d).subtract(1, "month").startOf("day").format("YYYY-MM-DD");
    const end = moment(d).add(1, "month").endOf("day").format("YYYY-MM-DD");
    dispatch(setloadeActive());
    baseUrl
      .get("/appointment/findAllAppointmentsByDoctor", {
        params: { startDate: start, endDate: end },
        headers: doctorId === "all" ? {} : { doctorid: doctorId },
      })
      .then(async (response) => {
        const data = response.data.data.map((element: any) => {
          const resourceId = element.resourceId ?? element.data?.appointment?.doctorId;
          return {
            ...element,
            start: moment(element.start).toDate(),
            end: moment(element.end).toDate(),
            resourceId: resourceId != null ? String(resourceId) : undefined,
          };
        });
        setTimeSlots(
          response.data.doctorWorkingHours ??
            response.data.workingGeneralHours ??
            []
        );
        setEvents(data);
        dispatch(setloadeDeactive());
      })
      .catch((error) => {
        console.error(error);
      });
  }, [dispatch, date]);

  const fetchTrayAppointments = useCallback(() => {
    dispatch(setloadeActive());
    baseUrl
      .get("/appointment/findAllAppointments", {
        headers: { status: "tray" },
      })
      .then((response) => {
        setTray(response.data.data);
        dispatch(setloadeDeactive());
      })
      .catch((error) => {
        console.error(error);
      });
  }, [dispatch]);

  useEffect(() => {
    // Handle online appointment updates
    if (Object.keys(onlineappointment).length > 0) {
      fetchAppointments(doctor);
      fetchTrayAppointments();
    }
  }, [onlineappointment, doctor, fetchAppointments, fetchTrayAppointments]);

  const timrInterval = useCallback(() => {
    baseUrl
      .get("/clinic/getTimeData")
      .then((response) => {
        if (response.data.data.startTime) {
          dispatch(
            setTimeInterval(
              {
                from: moment(
                  `2022-10-10T${response.data.data.startTime}`
                ).toDate(),
                to: moment(`2022-10-10T${response.data.data.endTime}`).toDate(),
              },
              false
            )
          );
        }
      })
      .catch((error) => {
        console.log(error);
      });
  }, [dispatch]);

  useEffect(() => {
    fetchTrayAppointments();
    fetchAppointments(doctor);
    if (newInterval?.intervalIsEMpty) timrInterval();

    baseUrl
      .get("/appointment/findAllAppointmentDentist")
      .then((response) => {
        setDoctors(response.data.data);
      })
      .catch((error) => {
        console.error(error);
      });
  }, [doctor, fetchAppointments, fetchTrayAppointments, newInterval, timrInterval]);

  const handleOnlineAppointmentClick = useCallback(
    (appointment: any) => {
      // Extract date from event start date or appointment date
      let appointmentDate = appointment?.date;
      if (!appointmentDate && appointment?._eventStart) {
        appointmentDate = formatDate(appointment._eventStart);
      }
      if (!appointmentDate) {
        appointmentDate = formatDate(new Date());
      }

      // Extract time from appointment or event
      let startTime = appointment?.startTime;
      let endTime = appointment?.endTime;
      if (!startTime && appointment?._eventStart) {
        startTime = formatTime(appointment._eventStart);
      }
      if (!endTime && appointment?._eventEnd) {
        endTime = formatTime(appointment._eventEnd);
      }

      // Clean up the appointment object (remove internal event fields)
      const { _eventStart, _eventEnd, ...cleanAppointment } = appointment;

      setOnlineAppointmentDetail({
        ...cleanAppointment,
        id: cleanAppointment?.id, // Store appointment ID for updating
        date: appointmentDate,
        startTime: startTime || cleanAppointment?.startTime || "",
        endTime: endTime || cleanAppointment?.endTime || "",
        doctorId: cleanAppointment?.doctorId || doctor,
      });
      setOnlineAppointmentModal(true);
    },
    [doctor, formatTime]
  );

  const reschedule = useCallback(
    (e: any) => {
      if (e.type === "online" && !e.added) {
        handleOnlineAppointmentClick(e);
      } else {
        if (selectReSchedule !== "") {
          toast.info(
            "Click an empty time on the calendar to set the new appointment time.",
            { autoClose: 5000 }
          );
          return;
        } else {
          setAppointmentType("Completed");
          setAppointmentDetail({
            action: "Completed",
            appointmentId: e?.id,
            isCompleted: e?.status === "completed",
          });
          setAppointmentModal(true);
        }
      }
    },
    [selectReSchedule, handleOnlineAppointmentClick]
  );

  const makeAppointment = useCallback(
    (e: any) => {
      // Require a doctor to be selected (or in day view, slot must have resourceId)
      if (doctor === "all") {
        toast.warning("Please select a doctor.");
        return;
      }

      // If tray has appointments, ONLY allow rescheduling - block new appointments
      if (tray && tray.length > 0) {
        // Must select from reschedule tray first
        if (!selectReSchedule) {
          toast.info(
            "Step 1: Select a patient in the reschedule tray above, then click an empty time on the calendar.",
            { autoClose: 6000 }
          );
          return;
        }

        // Validate time slot for rescheduling
        const isOverlapping = events.some(
          (event) => e.start < event.end && e.end > event.start
        );

        if (isOverlapping) {
          toast("This time slot is already booked.");
          return;
        }

        const now = new Date();
        const isPastDate = e.start.getTime() < now.getTime();

        if (isPastDate) {
          toast("You cannot make an appointment before now");
          return;
        }

        const startDayOfWeek = e.start.getDay();
        const startHours = e.start.getHours();
        const startMinutes = e.start.getMinutes();
        const isClickable = (timeSlots ?? []).some((interval: any) => {
          const intStart = interval.start;
          const intEnd = interval.end;
          return (
            interval.day === startDayOfWeek &&
            (startHours > intStart.hours ||
              (startHours === intStart.hours &&
                startMinutes >= intStart.minutes)) &&
            (startHours < intEnd.hours ||
              (startHours === intEnd.hours && startMinutes < intEnd.minutes))
          );
        });

        if (!isClickable) {
          toast("Selected time is not available.");
          return;
        }

        // Allow rescheduling when appointment is selected from tray
        setAppointmentType("Reschedule");
        const rescheduleMeta = slotMetaForSlot(timeSlots, e.start);
        setAppointmentDetail({
          ...e,
          action: "Reschedule",
          start: formatDate(new Date(e.start)),
          end: formatDate(new Date(e.end)),
          startTime: formatTime(e.start),
          endTime: formatTime(e.end),
          appointmentId: selectReSchedule,
          doctorId: view === "day" ? e.resourceId : doctor,
          fromRescheduleTray: true,
          slotPickedOnCalendar: true,
          ...(rescheduleMeta.sessionName
            ? { sessionName: rescheduleMeta.sessionName }
            : {}),
          ...(rescheduleMeta.roomNumber
            ? { roomNumber: rescheduleMeta.roomNumber }
            : {}),
        });
        setAppointmentModal(true);
        return; // Exit early - don't allow new appointments
      }

      // Tray is empty, allow normal flow (reschedule or new appointment)
      const isOverlapping = events.some(
        (event) => e.start < event.end && e.end > event.start
      );

      if (isOverlapping) {
        toast("This time slot is already booked.");
        return;
      }

      const now = new Date();
      const isPastDate = e.start.getTime() < now.getTime();

      if (isPastDate) {
        toast("You cannot make an appointment before now");
        return;
      }

      const startDayOfWeek = e.start.getDay();
      const startHours = e.start.getHours();
      const startMinutes = e.start.getMinutes();
      const isClickable = (timeSlots ?? []).some((interval: any) => {
        const intStart = interval.start;
        const intEnd = interval.end;
        return (
          interval.day === startDayOfWeek &&
          (startHours > intStart.hours ||
            (startHours === intStart.hours &&
              startMinutes >= intStart.minutes)) &&
          (startHours < intEnd.hours ||
            (startHours === intEnd.hours && startMinutes < intEnd.minutes))
        );
      });

      if (!isClickable) {
        toast("Selected time is not available.");
        return;
      }

      if (selectReSchedule) {
        setAppointmentType("Reschedule");
        const rescheduleMeta = slotMetaForSlot(timeSlots, e.start);
        setAppointmentDetail({
          ...e,
          action: "Reschedule",
          start: formatDate(new Date(e.start)),
          end: formatDate(new Date(e.end)),
          startTime: formatTime(e.start),
          endTime: formatTime(e.end),
          appointmentId: selectReSchedule,
          doctorId: view === "day" ? e.resourceId : doctor,
          fromRescheduleTray: true,
          slotPickedOnCalendar: true,
          ...(rescheduleMeta.sessionName
            ? { sessionName: rescheduleMeta.sessionName }
            : {}),
          ...(rescheduleMeta.roomNumber
            ? { roomNumber: rescheduleMeta.roomNumber }
            : {}),
        });
        setAppointmentModal(true);
      } else {
        // Allow making new appointment only when tray is empty (doctor required unless day view with column)
        if (doctor === "all" && (view !== Views.DAY || !e?.resourceId)) {
          toast.warning("Please select a doctor.");
          return;
        }

        setAppointmentType("schedule");

        const slotMeta = slotMetaForSlot(timeSlots, e.start);
        setNewAppointmentDetail({
          action: "Schedule",
          startTime: formatTime(e?.start),
          endTime: formatTime(e?.end),
          date: formatDate(new Date(e.start)),
          doctorId: view === "day" ? e.resourceId : doctor,
          ...(slotMeta.sessionName ? { sessionName: slotMeta.sessionName } : {}),
          ...(slotMeta.roomNumber ? { roomNumber: slotMeta.roomNumber } : {}),
        });
        setNewAppointmentModal(true);
      }
    },
    [
      doctor,
      selectReSchedule,
      timeSlots,
      view,
      events,
      tray,
      formatTime,
    ]
  );

  const onSave = useCallback(() => {
    fetchAppointments(doctor);
    fetchTrayAppointments();
    // Don't clear selectReSchedule here - it will be cleared after successful reschedule
    // setSelectReSchedule("");
  }, [doctor, fetchAppointments, fetchTrayAppointments]);

  const onDoctorSelected = useCallback(
    (e: any) => {
      setDoctor(e.target.value);
      fetchAppointments(e.target.value);
    },
    [fetchAppointments]
  );

  const dayPropGetter = useCallback(
    (date: Date) => {
      const dayOfWeek = date.getDay();
      const hours = date.getHours();
      const minutes = date.getMinutes();
      const isBlocked = (timeSlots ?? []).some((interval: any) => {
        const start = interval.start;
        const end = interval.end;
        return (
          interval.day === dayOfWeek &&
          (hours > start.hours ||
            (hours === start.hours && minutes >= start.minutes)) &&
          (hours < end.hours || (hours === end.hours && minutes < end.minutes))
        );
      });

      return isBlocked
        ? {
            style: {
              backgroundColor: "rgb(6 184 44 / 40%)",
              opacity: 0.5,
              cursor: "pointer",
            },
          }
        : { style: { backgroundColor: "", opacity: 0.5, cursor: "no-drop" } };
    },
    [timeSlots]
  );

  return (
    <div className="flex flex-col lg:flex-row gap-4">
      {/* Calendar Section */}
      <div className="flex-1 bg-white rounded-xl shadow-lg shadow-slate-200/50 border border-slate-100 overflow-hidden">
        {/* Header Controls */}
        
        <div className="p-4 border-b border-slate-100 bg-slate-50/50">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="relative z-20 flex items-center gap-2">
              <CalendarTodayIcon
                className="text-primary"
                sx={{ fontSize: 20 }}
              />
              <div className="[&_.react-datepicker-wrapper]:w-auto [&_input]:px-4 [&_input]:py-2 [&_input]:bg-white [&_input]:border [&_input]:border-slate-200 [&_input]:rounded-xl [&_input]:outline-none [&_input]:focus:ring-2 [&_input]:focus:ring-primary [&_input]:focus:border-transparent [&_input]:transition-all [&_input]:text-slate-700 [&_input]:font-medium">
                <DatePicker
                  selected={date}
                  onChange={(newDate: any) => setDate(newDate)}
                />
              </div>
            </div>
            <div className="text-2xl font-bold">Make An Appointment</div>
            {/* Doctor Select */}
            <div className="flex items-center gap-2">
              <PersonIcon className="text-primary" sx={{ fontSize: 20 }} />
              <select
                name="selectdoctor"
                onChange={(e: any) => onDoctorSelected(e)}
                className="px-4 py-2 bg-white border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-slate-700 font-medium min-w-[180px]"
              >
                <option value="all">All Doctors</option>
                {doctors.map((item: any, i: number) => (
                  <option key={i} value={item?.id}>
                    Dr. {item?.title + " " + item?.lastName}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        {/* Grid Layout: ReScheduleTray at top, Calendar below */}
        <div className="grid grid-cols-1 gap-4 p-1">
          {/* Reschedule Tray Section */}
          {tray.length > 0 && (
            <div className="bg-white rounded-2xl shadow-lg shadow-slate-200/50 border border-slate-100 overflow-hidden">
              <ReScheduleTray
                data={tray}
                selectedId={selectReSchedule}
                setSelectReSchedule={setSelectReSchedule}
                onCancel={fetchTrayAppointments}
              />
            </div>
          )}
          
          {/* Calendar */}
          <div className="overflow-auto [&_.rbc-timeslot-group]:min-h-[120px]">
            <Calendar
              events={events}
              defaultDate={"2022-10-10"}
              defaultView={Views.WEEK}
              resources={
                view === Views.DAY
                  ? (doctor === "all"
                      ? doctors.map((d: any) => ({
                          id: String(d?.id ?? d?._id),
                          title: `Dr. ${d?.title ?? ""} ${d?.lastName ?? ""}`.trim(),
                        }))
                      : doctors
                          .filter((d: any) => String(d?.id ?? d?._id) === String(doctor))
                          .map((d: any) => ({
                            id: String(d?.id ?? d?._id),
                            title: `Dr. ${d?.title ?? ""} ${d?.lastName ?? ""}`.trim(),
                          })))
                  : undefined
              }
              min={newInterval?.interval?.from}
              max={newInterval?.interval?.to}
              onSelectSlot={view !== Views.MONTH ? makeAppointment : undefined}
              components={view !== Views.MONTH ? components : undefined}
              slotPropGetter={dayPropGetter}
              startAccessor="start"
              endAccessor="end"
              date={date}
              view={view}
              onView={setView}
              onNavigate={setDate}
              step={STEP}
              timeslots={TIME_SLOTS}
              selectable
            />
          </div>
        </div>
        {appointmentModal && (
          <AppointmentsSchedule
            doctor={doctors}
            appointmentDetail={appointmentDetail}
            appointmentType={appointmentType}
            setappointmentModal={setAppointmentModal}
            onsave={onSave}
            selectReSchedule={selectReSchedule}
            setSelectReSchedule={setSelectReSchedule}
            timeSlots={timeSlots}
            events={events}
            tray={tray}
            setAppointmentDetail={setAppointmentDetail}
            setAppointmentType={setAppointmentType}
          />
        )}
        {newAppointmentModal && (
          <NewAppointment
            newappointmentDetail={newappointmentDetail}
            setNewAppointmentModal={setNewAppointmentModal}
            doctor={doctors}
            onsave={onSave}
          />
        )}
        {onlineAppointmentModal && (
          <OnlineAppointmentModal
            onlineAppointment={onlineAppointmentDetail}
            setOnlineAppointmentModal={setOnlineAppointmentModal}
            doctor={doctors}
            onsave={onSave}
          />
        )}
      </div>
      
    </div>
  );
}
